'use client';

import { createClient } from '@/lib/supabase/client';

export type PosRole =
  | 'super_admin' |'admin' |'manager' |'inventory_manager' |'sales_manager' |'supervisor' |'cashier';

export interface UserPermissions {
  manageProducts: boolean;
  manageInventory: boolean;
  viewReports: boolean;
  manageCashiers: boolean;
  manageSettings: boolean;
  voidSales: boolean;
  applyDiscounts: boolean;
}

export interface PosUser {
  id: string;
  name: string;
  username: string;
  role: PosRole;
  profilePicture?: string;
  permissions: UserPermissions;
}

// ─── Role helpers ─────────────────────────────────────────────────────────────

export const CASHIER_ROLES: PosRole[] = ['cashier'];

export const ADMIN_ROLES: PosRole[] = [
  'super_admin',
  'admin',
  'manager',
  'inventory_manager',
  'sales_manager',
  'supervisor',
];

export function isCashierRole(role: PosRole): boolean {
  return CASHIER_ROLES.includes(role);
}

export function isAdminRole(role: PosRole): boolean {
  return ADMIN_ROLES.includes(role);
}

export function getRoleLabel(role: PosRole): string {
  const labels: Record<PosRole, string> = {
    super_admin: 'Super Admin',
    admin: 'Administrator',
    manager: 'Manager',
    inventory_manager: 'Inventory Manager',
    sales_manager: 'Sales Manager',
    supervisor: 'Supervisor',
    cashier: 'Cashier',
  };
  return labels[role] ?? role;
}

// ─── Default permissions per role ────────────────────────────────────────────

export function getDefaultPermissions(role: PosRole): UserPermissions {
  switch (role) {
    case 'super_admin': case'admin':
      return {
        manageProducts: true,
        manageInventory: true,
        viewReports: true,
        manageCashiers: true,
        manageSettings: true,
        voidSales: true,
        applyDiscounts: true,
      };
    case 'manager':
      return {
        manageProducts: true,
        manageInventory: true,
        viewReports: true,
        manageCashiers: true,
        manageSettings: true,
        voidSales: true,
        applyDiscounts: true,
      };
    case 'inventory_manager':
      return {
        manageProducts: true,
        manageInventory: true,
        viewReports: true,
        manageCashiers: false,
        manageSettings: false,
        voidSales: false,
        applyDiscounts: false,
      };
    case 'sales_manager':
      return {
        manageProducts: false,
        manageInventory: false,
        viewReports: true,
        manageCashiers: false,
        manageSettings: false,
        voidSales: true,
        applyDiscounts: true,
      };
    case 'supervisor':
      return {
        manageProducts: true,
        manageInventory: true,
        viewReports: true,
        manageCashiers: false,
        manageSettings: false,
        voidSales: true,
        applyDiscounts: true,
      };
    case 'cashier':
    default:
      return {
        manageProducts: false,
        manageInventory: false,
        viewReports: false,
        manageCashiers: false,
        manageSettings: false,
        voidSales: false,
        applyDiscounts: true,
      };
  }
}

// ─── Route permission map ─────────────────────────────────────────────────────

export const ROUTE_PERMISSIONS: Record<string, (user: PosUser) => boolean> = {
  '/admin-dashboard': (u) => isAdminRole(u.role),
  '/product-management': (u) => isAdminRole(u.role) && u.permissions.manageProducts,
  '/inventory-management': (u) => isAdminRole(u.role) && u.permissions.manageInventory,
  '/transaction-history': (u) => isAdminRole(u.role) && u.permissions.viewReports,
  '/cashier-management': (u) => isAdminRole(u.role) && u.permissions.manageCashiers,
  '/user-management': (u) => isAdminRole(u.role) && u.permissions.manageCashiers,
  '/settings': (u) => isAdminRole(u.role) && u.permissions.manageSettings,
  '/sales-screen': () => true, // all roles can access sales screen
};

export function canAccessRoute(user: PosUser, route: string): boolean {
  const checker = ROUTE_PERMISSIONS[route];
  if (!checker) return isAdminRole(user.role); // unknown routes: admin only
  return checker(user);
}

// ─── Session storage ──────────────────────────────────────────────────────────

const SESSION_KEY = 'pos_session';

export function getPosSession(): PosUser | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setPosSession(user: PosUser): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(SESSION_KEY, JSON.stringify(user));
}

export function clearPosSession(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(SESSION_KEY);
}

// ─── Login ────────────────────────────────────────────────────────────────────

export async function loginWithUsernamePin(
  username: string,
  pin: string
): Promise<PosUser | null> {
  const supabase = createClient();
  const { data, error } = await supabase.rpc('verify_pin_by_username', {
    p_username: username.trim(),
    p_pin: pin.trim(),
  });
  if (error || !data || data.length === 0) return null;
  const row = data[0];
  if (!row.is_active) return null;

  const role = row.role as PosRole;
  const dbPerms = row.permissions as Record<string, boolean> | null;

  // Merge DB permissions with role defaults
  const defaultPerms = getDefaultPermissions(role);
  const permissions: UserPermissions =
    dbPerms && Object.keys(dbPerms).length > 0
      ? {
          manageProducts: dbPerms.manageProducts ?? defaultPerms.manageProducts,
          manageInventory: dbPerms.manageInventory ?? defaultPerms.manageInventory,
          viewReports: dbPerms.viewReports ?? defaultPerms.viewReports,
          manageCashiers: dbPerms.manageCashiers ?? defaultPerms.manageCashiers,
          manageSettings: dbPerms.manageSettings ?? defaultPerms.manageSettings,
          voidSales: dbPerms.voidSales ?? defaultPerms.voidSales,
          applyDiscounts: dbPerms.applyDiscounts ?? defaultPerms.applyDiscounts,
        }
      : defaultPerms;

  return {
    id: row.id,
    name: row.name,
    username: row.username ?? username,
    role,
    profilePicture: row.profile_picture ?? undefined,
    permissions,
  };
}

// Legacy PIN-only login (kept for backward compatibility)
export async function loginWithPin(pin: string): Promise<{ id: string; name: string; role: string } | null> {
  const supabase = createClient();
  const { data, error } = await supabase.rpc('verify_pin', { p_pin: pin });
  if (error || !data || data.length === 0) return null;
  const row = data[0];
  if (!row.is_active) return null;
  return { id: row.id, name: row.name, role: row.role };
}
